import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  AppBar,
  Toolbar,
  Avatar,
  TextField,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import settingsService, { SystemSettings } from '../services/settingsService';

const SettingsPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState<'success' | 'error'>('success');
  const [alertMessage, setAlertMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<SystemSettings>({
    university: {
      universityName: '',
      contactEmail: '',
      phoneNumber: '',
      address: '',
      psychologyEmail: '',
      counselingEmail: '',
      mentalHealthEmail: '',
      weekdayHours: '',
      weekendHours: ''
    },
    assessment: {
      defaultTimeLimit: 30,
      highRiskThreshold: 75,
      moderateRiskThreshold: 50,
      allowRetakes: true,
      requireCompletion: true,
      generateWeeklySummary: true,
      generateMonthlyAnalytics: true,
      generateQuarterlyReports: false,
      assessmentRetentionMonths: 24,
      activityLogRetentionMonths: 12
    },
    notification: {
      highRiskAlerts: true,
      assessmentCompletionAlerts: true,
      weeklySummaryEmails: false,
      maintenanceAlerts: true,
      adminAlertEmail: '',
      highRiskAlertDelay: 0,
      assessmentReminderDays: 3,
      overdueAlertDays: 1,
      enableEmergencyContact: true,
      emergencyContactNumber: '911'
    },
    security: {
      minPasswordLength: 8,
      requireSpecialChars: true,
      requireNumbers: true,
      requireMixedCase: false,
      passwordExpiryDays: 90,
      sessionTimeoutMinutes: 60,
      maxLoginAttempts: 5,
      lockoutDurationMinutes: 30,
      enableEncryption: true,
      enableAuditLogging: true,
      enableTwoFactor: false
    }
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await settingsService.getSettings();
      setSettings(data);
    } catch (error) {
      console.error('Failed to load settings:', error);
      showAlertMessage('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  const showAlertMessage = (message: string, type: 'success' | 'error' = 'success') => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 3000);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await settingsService.updateAllSettings(settings);
      showAlertMessage('Settings saved successfully!', 'success');
    } catch (error) {
      console.error('Failed to save settings:', error);
      showAlertMessage('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateUniversitySetting = (field: keyof typeof settings.university, value: string) => {
    setSettings(prev => ({
      ...prev,
      university: { ...prev.university, [field]: value }
    }));
  };

  const updateAssessmentSetting = (field: keyof typeof settings.assessment, value: any) => {
    setSettings(prev => ({
      ...prev,
      assessment: { ...prev.assessment, [field]: value }
    }));
  };

  const updateNotificationSetting = (field: keyof typeof settings.notification, value: any) => {
    setSettings(prev => ({
      ...prev,
      notification: { ...prev.notification, [field]: value }
    }));
  };

  const updateSecuritySetting = (field: keyof typeof settings.security, value: any) => {
    setSettings(prev => ({
      ...prev,
      security: { ...prev.security, [field]: value }
    }));
  };

  const TabPanel = ({ children, value, index }: any) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ mt: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Psychology Profile System - System Settings
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              {user?.firstName?.charAt(0) || 'U'}
            </Avatar>
            <Typography variant="body1">
              {user?.firstName} {user?.lastName}
            </Typography>
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button variant="outlined" onClick={handleBack}>
              ← Back to Dashboard
            </Button>
            <Typography variant="h4">
              System Settings
            </Typography>
          </Box>
          <Button 
            variant="contained" 
            onClick={handleSave}
            disabled={loading || saving}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>

        {showAlert && (
          <Alert severity={alertType} sx={{ mb: 3 }}>
            {alertMessage}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        <Card>
          <CardContent>
            <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
              <Tab label="University Settings" />
              <Tab label="Assessment Configuration" />
              <Tab label="Notification Settings" />
              <Tab label="Security Settings" />
            </Tabs>

            {/* University Settings Tab */}
            <TabPanel value={currentTab} index={0}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="University Name"
                    value={settings.university.universityName}
                    onChange={(e) => updateUniversitySetting('universityName', e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  
                  <TextField
                    fullWidth
                    label="Contact Email"
                    value={settings.university.contactEmail}
                    onChange={(e) => updateUniversitySetting('contactEmail', e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={settings.university.phoneNumber}
                    onChange={(e) => updateUniversitySetting('phoneNumber', e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Address"
                    value={settings.university.address}
                    onChange={(e) => updateUniversitySetting('address', e.target.value)}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Department Configuration
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      label="Psychology Department Email"
                      value={settings.university.psychologyEmail}
                      onChange={(e) => updateUniversitySetting('psychologyEmail', e.target.value)}
                      sx={{ mb: 1 }}
                    />
                    <TextField
                      fullWidth
                      label="Counseling Services Email"
                      value={settings.university.counselingEmail}
                      onChange={(e) => updateUniversitySetting('counselingEmail', e.target.value)}
                      sx={{ mb: 1 }}
                    />
                    <TextField
                      fullWidth
                      label="Mental Health Center Email"
                      value={settings.university.mentalHealthEmail}
                      onChange={(e) => updateUniversitySetting('mentalHealthEmail', e.target.value)}
                      sx={{ mb: 1 }}
                    />
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="h6" gutterBottom>
                    Operating Hours
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="Weekday Hours"
                    value={settings.university.weekdayHours}
                    onChange={(e) => updateUniversitySetting('weekdayHours', e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    fullWidth
                    label="Weekend Hours"
                    value={settings.university.weekendHours}
                    onChange={(e) => updateUniversitySetting('weekendHours', e.target.value)}
                    sx={{ mb: 1 }}
                  />
                </Grid>
              </Grid>
            </TabPanel>

            {/* Assessment Configuration Tab */}
            <TabPanel value={currentTab} index={1}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Assessment Defaults
                  </Typography>
                  
                  <TextField
                    fullWidth
                    type="number"
                    label="Default Assessment Time Limit (minutes)"
                    value={settings.assessment.defaultTimeLimit}
                    onChange={(e) => updateAssessmentSetting('defaultTimeLimit', parseInt(e.target.value) || 30)}
                    sx={{ mb: 2 }}
                  />
                  
                  <TextField
                    fullWidth
                    type="number"
                    label="High Risk Threshold (%)"
                    value={settings.assessment.highRiskThreshold}
                    onChange={(e) => updateAssessmentSetting('highRiskThreshold', parseInt(e.target.value) || 75)}
                    sx={{ mb: 2 }}
                  />
                  
                  <TextField
                    fullWidth
                    type="number"
                    label="Moderate Risk Threshold (%)"
                    value={settings.assessment.moderateRiskThreshold}
                    onChange={(e) => updateAssessmentSetting('moderateRiskThreshold', parseInt(e.target.value) || 50)}
                    sx={{ mb: 2 }}
                  />
                  
                  <FormControlLabel
                    control={<Switch checked={settings.assessment.allowRetakes} onChange={(e) => updateAssessmentSetting('allowRetakes', e.target.checked)} />}
                    label="Allow Assessment Retakes"
                    sx={{ mb: 2 }}
                  />
                  
                  <FormControlLabel
                    control={<Switch checked={settings.assessment.requireCompletion} onChange={(e) => updateAssessmentSetting('requireCompletion', e.target.checked)} />}
                    label="Require Assessment Completion"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Auto-Generated Reports
                  </Typography>
                  
                  <FormControlLabel
                    control={<Switch checked={settings.assessment.generateWeeklySummary} onChange={(e) => updateAssessmentSetting('generateWeeklySummary', e.target.checked)} />}
                    label="Generate Weekly Summary Reports"
                    sx={{ mb: 2 }}
                  />
                  
                  <FormControlLabel
                    control={<Switch checked={settings.assessment.generateMonthlyAnalytics} onChange={(e) => updateAssessmentSetting('generateMonthlyAnalytics', e.target.checked)} />}
                    label="Generate Monthly Analytics"
                    sx={{ mb: 2 }}
                  />
                  
                  <FormControlLabel
                    control={<Switch checked={settings.assessment.generateQuarterlyReports} onChange={(e) => updateAssessmentSetting('generateQuarterlyReports', e.target.checked)} />}
                    label="Generate Quarterly Risk Reports"
                    sx={{ mb: 2 }}
                  />
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="h6" gutterBottom>
                    Data Retention
                  </Typography>
                  
                  <TextField
                    fullWidth
                    type="number"
                    label="Assessment Data Retention (months)"
                    value={settings.assessment.assessmentRetentionMonths}
                    onChange={(e) => updateAssessmentSetting('assessmentRetentionMonths', parseInt(e.target.value) || 24)}
                    sx={{ mb: 2 }}
                  />
                  
                  <TextField
                    fullWidth
                    type="number"
                    label="User Activity Logs Retention (months)"
                    value={settings.assessment.activityLogRetentionMonths}
                    onChange={(e) => updateAssessmentSetting('activityLogRetentionMonths', parseInt(e.target.value) || 12)}
                    sx={{ mb: 2 }}
                  />
                </Grid>
              </Grid>
            </TabPanel>

            {/* Notification Settings Tab */}
            <TabPanel value={currentTab} index={2}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Email Notifications
                  </Typography>
                  
                  <FormControlLabel
                    control={<Switch checked={settings.notification.highRiskAlerts} onChange={(e) => updateNotificationSetting('highRiskAlerts', e.target.checked)} />}
                    label="High Risk Alert Notifications"
                    sx={{ mb: 1 }}
                  />
                  
                  <FormControlLabel
                    control={<Switch checked={settings.notification.assessmentCompletionAlerts} onChange={(e) => updateNotificationSetting('assessmentCompletionAlerts', e.target.checked)} />}
                    label="Assessment Completion Notifications"
                    sx={{ mb: 1 }}
                  />
                  
                  <FormControlLabel
                    control={<Switch checked={settings.notification.weeklySummaryEmails} onChange={(e) => updateNotificationSetting('weeklySummaryEmails', e.target.checked)} />}
                    label="Weekly Summary Emails"
                    sx={{ mb: 1 }}
                  />
                  
                  <FormControlLabel
                    control={<Switch checked={settings.notification.maintenanceAlerts} onChange={(e) => updateNotificationSetting('maintenanceAlerts', e.target.checked)} />}
                    label="System Maintenance Alerts"
                    sx={{ mb: 2 }}
                  />
                  
                  <TextField
                    fullWidth
                    label="Admin Email for Alerts"
                    value={settings.notification.adminAlertEmail}
                    onChange={(e) => updateNotificationSetting('adminAlertEmail', e.target.value)}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Alert Timing
                  </Typography>
                  
                  <TextField
                    fullWidth
                    type="number"
                    label="High Risk Alert Delay (minutes)"
                    value={settings.notification.highRiskAlertDelay}
                    onChange={(e) => updateNotificationSetting('highRiskAlertDelay', parseInt(e.target.value) || 0)}
                    helperText="0 = Immediate notification"
                    sx={{ mb: 2 }}
                  />
                  
                  <TextField
                    fullWidth
                    type="number"
                    label="Assessment Reminder (days before due)"
                    value={settings.notification.assessmentReminderDays}
                    onChange={(e) => updateNotificationSetting('assessmentReminderDays', parseInt(e.target.value) || 3)}
                    sx={{ mb: 2 }}
                  />
                  
                  <TextField
                    fullWidth
                    type="number"
                    label="Overdue Assessment Alert (days after due)"
                    value={settings.notification.overdueAlertDays}
                    onChange={(e) => updateNotificationSetting('overdueAlertDays', parseInt(e.target.value) || 1)}
                    sx={{ mb: 2 }}
                  />
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="h6" gutterBottom>
                    Emergency Protocols
                  </Typography>
                  
                  <FormControlLabel
                    control={<Switch checked={settings.notification.enableEmergencyContact} onChange={(e) => updateNotificationSetting('enableEmergencyContact', e.target.checked)} />}
                    label="Enable Emergency Contact System"
                    sx={{ mb: 1 }}
                  />
                  
                  <TextField
                    fullWidth
                    label="Emergency Contact Number"
                    value={settings.notification.emergencyContactNumber}
                    onChange={(e) => updateNotificationSetting('emergencyContactNumber', e.target.value)}
                    sx={{ mb: 2 }}
                  />
                </Grid>
              </Grid>
            </TabPanel>

            {/* Security Settings Tab */}
            <TabPanel value={currentTab} index={3}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Password Policy
                  </Typography>
                  
                  <TextField
                    fullWidth
                    type="number"
                    label="Minimum Password Length"
                    value={settings.security.minPasswordLength}
                    onChange={(e) => updateSecuritySetting('minPasswordLength', parseInt(e.target.value) || 8)}
                    sx={{ mb: 2 }}
                  />
                  
                  <FormControlLabel
                    control={<Switch checked={settings.security.requireSpecialChars} onChange={(e) => updateSecuritySetting('requireSpecialChars', e.target.checked)} />}
                    label="Require Special Characters"
                    sx={{ mb: 1 }}
                  />
                  
                  <FormControlLabel
                    control={<Switch checked={settings.security.requireNumbers} onChange={(e) => updateSecuritySetting('requireNumbers', e.target.checked)} />}
                    label="Require Numbers"
                    sx={{ mb: 1 }}
                  />
                  
                  <FormControlLabel
                    control={<Switch checked={settings.security.requireMixedCase} onChange={(e) => updateSecuritySetting('requireMixedCase', e.target.checked)} />}
                    label="Require Mixed Case"
                    sx={{ mb: 2 }}
                  />
                  
                  <TextField
                    fullWidth
                    type="number"
                    label="Password Expiry (days)"
                    value={settings.security.passwordExpiryDays}
                    onChange={(e) => updateSecuritySetting('passwordExpiryDays', parseInt(e.target.value) || 90)}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Session Management
                  </Typography>
                  
                  <TextField
                    fullWidth
                    type="number"
                    label="Session Timeout (minutes)"
                    value={settings.security.sessionTimeoutMinutes}
                    onChange={(e) => updateSecuritySetting('sessionTimeoutMinutes', parseInt(e.target.value) || 60)}
                    sx={{ mb: 2 }}
                  />
                  
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Login Attempts"
                    value={settings.security.maxLoginAttempts}
                    onChange={(e) => updateSecuritySetting('maxLoginAttempts', parseInt(e.target.value) || 5)}
                    sx={{ mb: 2 }}
                  />
                  
                  <TextField
                    fullWidth
                    type="number"
                    label="Account Lockout Duration (minutes)"
                    value={settings.security.lockoutDurationMinutes}
                    onChange={(e) => updateSecuritySetting('lockoutDurationMinutes', parseInt(e.target.value) || 30)}
                    sx={{ mb: 2 }}
                  />
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="h6" gutterBottom>
                    Data Privacy
                  </Typography>
                  
                  <FormControlLabel
                    control={<Switch checked={settings.security.enableEncryption} onChange={(e) => updateSecuritySetting('enableEncryption', e.target.checked)} />}
                    label="Enable Data Encryption"
                    sx={{ mb: 1 }}
                  />
                  
                  <FormControlLabel
                    control={<Switch checked={settings.security.enableAuditLogging} onChange={(e) => updateSecuritySetting('enableAuditLogging', e.target.checked)} />}
                    label="Enable Audit Logging"
                    sx={{ mb: 1 }}
                  />
                  
                  <FormControlLabel
                    control={<Switch checked={settings.security.enableTwoFactor} onChange={(e) => updateSecuritySetting('enableTwoFactor', e.target.checked)} />}
                    label="Enable Two-Factor Authentication"
                    sx={{ mb: 2 }}
                  />
                </Grid>
              </Grid>
            </TabPanel>
          </CardContent>
        </Card>
      </Container>
    </>
  );
};

export default SettingsPage;